import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Button, EditableText, Icon, Intent, NonIdealState, Switch } from '@blueprintjs/core';
import { styled, Pagination } from '@moleculeone/ui';
import { useDispatch, useSelector } from 'react-redux';
import { ReportItem as ReportItemType } from '@retro-ui/prisma';

import { AppToaster } from '@f/components/notification-toaster/AppToaster';
import { apiQueryClient } from '@f/api-client';
import { useThrottle } from '@f/hooks/useThrottle';
import { PdfDownloadButton } from '@f/components/pages/dashboard/reports/components/PdfDownloadButton';
import {
  getReport,
  patchReport,
  patchReportItem,
  removeReportItems,
  enableReportDesigningMode,
  disableReportDesigningMode,
} from '@f/services/report/report.actions';
import { isDesigningModeEnabled } from '@f/services/report/report.selectors';
import { withSelectability } from '@f/components/higher-order';
import Toolbar from '@f/components/shared/toolbar/Toolbar';
import LoadingScreen from '@f/components/shared/loading-screen/LoadingScreen';
import { pluralize } from '@f/utils/pluralize';

import ReportItem from './components/ReportItem';
import EmptyList from './components/EmptyList';

const REPORT_ITEMS_PAGE_SIZE = 10;
const REMOVE_REPORT_ITEM_TOAST_KEY = 'remove_report_item_toast';

const Page = styled('div', {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
});

const StyledToolbarItem = styled(Toolbar.Item, {
  display: 'flex',
  alignItems: 'center',
});

const StyledSwitch = styled(Switch, {
  margin: 0,
});

const InfoIcon = styled(Icon, { marginRight: '0.5rem' });

const Content = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  gap: '1.5rem',
  paddingBottom: '1rem',
});

const TitleInput = styled(EditableText, {
  fontSize: '2rem',
});

const ListHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  width: 'inherit',
});

interface SingleReportPageProps {
  selectionMap: {
    [itemId: string]: boolean;
  };
  selectionMode: boolean;
  toggleItemSelection: (item: ReportItemType) => void;
  deselectAllItems: () => void;
}

function SingleReportPage(props: SingleReportPageProps) {
  const queryClient = useQueryClient();

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { selectionMap, selectionMode, toggleItemSelection, deselectAllItems } = props;

  const { reportId } = useParams<{ reportId: string }>();

  const [movingItem, setMovingItem] = useState<ReportItemType | null>(null);
  const [isDesigningAlertOpen, setIsDesigningAlertOpen] = useState(false);

  const report = useSelector((state: any) => state.report.data[reportId]);
  const isGettingReport = useSelector((state: any) => state.report.get.processing > 0);
  const isDesigningMode = useSelector((state: any) => isDesigningModeEnabled(state));
  const designingId = useSelector((state: any) => state.report.designingId);

  const [page, setPage] = useState(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const page = urlSearchParams.get('page');
    if (page) {
      return Number(page);
    }
    return 1;
  });

  useEffect(() => {
    dispatch(getReport(reportId));
  }, [dispatch, reportId]);

  const itemsQueryParams = useMemo(() => {
    const query = { take: REPORT_ITEMS_PAGE_SIZE, skip: (page - 1) * REPORT_ITEMS_PAGE_SIZE };
    return query;
  }, [page]);

  useEffect(() => {
    const searchParams = new URLSearchParams([]);
    searchParams.set('page', String(page));
    const newSearchParamsString = searchParams.toString();

    history.replace({
      search: newSearchParamsString,
    });
  }, [history, page]);

  const itemsQueryKey = useMemo(() => ['items', reportId, page], [reportId, page]);
  const throttledQueryKey = useThrottle(itemsQueryKey);
  const itemsQuery = apiQueryClient.report.fetchReportItems.useQuery(
    throttledQueryKey,
    {
      params: {
        id: reportId,
      },
      query: itemsQueryParams,
    },
    {
      placeholderData: keepPreviousData,
      queryKey: throttledQueryKey,
    }
  );

  const items = itemsQuery.data?.body.data ?? [];

  const pagination = itemsQuery.data?.body.pagination;

  const showPagination = pagination && items.length > 0;

  const isThisDesigning = isDesigningMode && designingId === reportId;
  const isMovingMode = Boolean(movingItem);

  const updateItems = async () => {
    await queryClient.invalidateQueries({ queryKey: ['items', reportId] });
    itemsQuery.refetch();
  };

  const updateTitle = (value: string) => {
    dispatch(patchReport(reportId, { title: value }));
  };

  const updateComment = (itemId: string, text: string) => {
    dispatch(patchReportItem(reportId, itemId, { payload: { commentText: text } }));
  };

  const changeDesigningMode = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      if (!isDesigningMode) {
        dispatch(enableReportDesigningMode(reportId));
      } else {
        setIsDesigningAlertOpen(true);
      }
    } else {
      dispatch(disableReportDesigningMode());
    }
  };

  const enableMovingMode = (itemId: string) => {
    const item = items.find((item: ReportItemType) => item.id === itemId);
    if (item) {
      setMovingItem(item);
    }
  };

  const disableMovingMode = () => {
    setMovingItem(null);
  };

  const switchMovingMode = (shouldBeEnabled: boolean, itemId: string) => {
    if (shouldBeEnabled) {
      enableMovingMode(itemId);
    } else {
      disableMovingMode();
    }
  };

  const move = async (targetOrdinal: number) => {
    const isMovingMode = Boolean(movingItem);

    if (!isMovingMode) {
      return;
    }

    dispatch(patchReportItem(reportId, movingItem?.id, { ordinal: targetOrdinal }));
    disableMovingMode();
    await updateItems();
  };

  const moveStep = async (itemId: string, ordinalDiff: number) => {
    const item = items.find((item: ReportItemType) => item.id === itemId);

    if (!item) {
      return;
    }

    dispatch(patchReportItem(reportId, item.id, { ordinal: Number(item.ordinal) + ordinalDiff }));
    await updateItems();
  };

  const removeItems = async (reportId: string, itemIds: string[]) => {
    try {
      dispatch(removeReportItems(reportId, itemIds));
    } catch (error) {
      AppToaster.show(
        {
          intent: Intent.DANGER,
          icon: 'trash',
          message: 'Error removing report elements',
        },
        REMOVE_REPORT_ITEM_TOAST_KEY
      );
      return;
    }
    AppToaster.show(
      {
        intent: Intent.SUCCESS,
        icon: 'trash',
        message: `${pluralize(
          itemIds.length,
          'Report element has',
          `${itemIds.length} report elements have`
        )} been removed`,
      },
      REMOVE_REPORT_ITEM_TOAST_KEY
    );
    await updateItems();
  };

  const removeSelected = async () => {
    if (!selectionMode) {
      return;
    }
    const selectedIds = Object.keys(selectionMap).filter(id => selectionMap[id]);
    removeItems(reportId, selectedIds);
    deselectAllItems();
  };

  const remove = async (itemId: string) => {
    removeItems(reportId, [itemId]);
  };

  const confirmDesigningAlert = () => {
    dispatch(enableReportDesigningMode(reportId));
  };

  const closeDesigningAlert = () => {
    setIsDesigningAlertOpen(false);
  };

  if (isGettingReport) {
    return <LoadingScreen caption="Loading report elements..." />;
  }

  if (!report || !items) {
    return <NonIdealState title="Failed to load report" icon="circle" />;
  }
  return (
    <Page data-test="single-report-page">
      <Toolbar>
        <Toolbar.Item>
          <PdfDownloadButton reportId={reportId} disabled={items.length === 0} text={'Download PDF'} large />
        </Toolbar.Item>

        <Toolbar.Item>
          <Button
            large
            minimal
            disabled={!selectionMode}
            text="Remove"
            icon="trash"
            onClick={removeSelected}
            data-cy="button-report-remove-items"
          />
        </Toolbar.Item>

        <StyledToolbarItem>
          <StyledSwitch
            large
            checked={isThisDesigning}
            label="Editing mode"
            onChange={changeDesigningMode}
            data-cy="switch-report-editing-mode"
          />
        </StyledToolbarItem>

        <Toolbar.Item light alignRight>
          <InfoIcon icon="info-sign" />
          <span>
            In order to add elements, turn editing mode on and navigate to <Link to="/dashboard/batch">searches</Link>.
          </span>
        </Toolbar.Item>
      </Toolbar>

      <Content>
        <ListHeader>
          <TitleInput
            selectAllOnFocus
            disabled={!isThisDesigning}
            defaultValue={report.title}
            placeholder={isThisDesigning ? 'Add title...' : 'No title'}
            onConfirm={value => updateTitle(value)}
            data-test="title-input"
          />
          {showPagination && (
            <Pagination
              currentPage={page}
              itemCount={pagination.total}
              pageSize={REPORT_ITEMS_PAGE_SIZE}
              onChange={setPage}
            />
          )}
        </ListHeader>
        {items.length > 0 && (
          <>
            {items.map((item, i) => (
              <ReportItem
                data={item}
                isLast={i === items.length - 1}
                isThisDesigning={isThisDesigning}
                isSelectionMode={selectionMode}
                isSelected={Boolean(selectionMap[item.id])}
                isMovingMode={isMovingMode}
                isThisMoving={isMovingMode && item.id === movingItem?.id}
                movingOrdinal={isMovingMode ? movingItem?.ordinal : null}
                onToggleSelected={() => toggleItemSelection(item)}
                onMovingModeSwitch={switchMovingMode}
                onMove={move}
                onMoveStep={moveStep}
                onCommentUpdate={updateComment}
                onRemove={() => remove(item.id)}
                key={item.id}
              />
            ))}
            {showPagination && (
              <Pagination
                currentPage={page}
                itemCount={pagination.total}
                pageSize={REPORT_ITEMS_PAGE_SIZE}
                onChange={setPage}
                scrollTo={document.documentElement}
              />
            )}
          </>
        )}

        {items.length === 0 && (
          <EmptyList isThisDesigning={isThisDesigning} onDesigningModeChange={changeDesigningMode} />
        )}
      </Content>

      <Alert
        canEscapeKeyCancel
        canOutsideClickCancel
        intent={Intent.PRIMARY}
        isOpen={isDesigningAlertOpen}
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        icon="info-sign"
        onClose={closeDesigningAlert}
        onConfirm={confirmDesigningAlert}
      >
        You're already editing another report. Turning editing mode on for the currently seen report will turn it off
        for another report. Are you sure you want to continue?
      </Alert>
    </Page>
  );
}

export default withSelectability()(SingleReportPage);
